import { createMemo, createUniqueId, Index, Show } from "solid-js";
import type { Issue } from "../state/validation";
import { useTranslation } from "../state/i18n";
import uuid from "../utils/uuid";
import { isEmpty } from "lodash-es";
import Input, { ErrorMessage } from "../components/inputs/Input";
import SplitButtonProperty from "../components/button/SplitButtonProperty";
import { MAX_COLOURS } from "../constants";
import InlineMessage from "../components/inlineMessage/InlineMessage";
import { TextAndIconButton } from "../components/button/Button";
import { CompactTextInput } from "../components/inputs/TextInput";
import ColorInput from "../components/inputs/ColorInput";

const NEW_DEFAULT_COLOR = "#548B5F";

export type QueryColour = {
  query: string;
  id: string;
  color: string;
};

type SearchColoursInputProps = {
  queryColours: QueryColour[];
  onChange: (queryColours: QueryColour[]) => void;
  error: string | undefined;
  nestedErrors: Issue[] | undefined;
};

const SearchColoursInput = (props: SearchColoursInputProps) => {
  const { t } = useTranslation();

  const handleChange = (id: string, patch: Partial<QueryColour>) => {
    const next = props.queryColours.map((qc) => (qc.id === id ? { ...qc, ...patch } : { ...qc }));
    props.onChange(next);
  };

  const handleDelete = (id: string) => {
    const next = props.queryColours.filter((qc) => qc.id !== id);
    props.onChange(next);
  };

  const handleAdd = () => {
    const next = [...props.queryColours, { id: uuid(), color: NEW_DEFAULT_COLOR, query: "" }];
    props.onChange(next);
  };

  return (
    <Input
      info={{
        title: t(($) => $.FIELD_PROPERTY_SEARCH_COLORS),
        alt: t(($) => $.FIELD_PROPERTY_SEARCH_COLORS),
        description: t(($) => $.FIELD_PROPERTY_SEARCH_COLORS_DESCRIPTION),
      }}
      label={t(($) => $.FIELD_PROPERTY_SEARCH_COLORS)}
      errorMessage={props.error}
    >
      {() => (
        <>
          <ul class="flex flex-col gap-y-1">
            <Index each={props.queryColours}>
              {(queryColour, index) => {
                const issues = () =>
                  props.nestedErrors?.filter((issue) => (issue.path ?? []).some((p) => p.key === index));
                const queryError = createMemo(
                  () => issues()?.find((issue) => (issue.path ?? []).some((p) => p.key === "query"))?.message,
                );
                return (
                  <li>
                    <SplitButtonProperty
                      ariaLabel={t(($) => $.FIELD_PROPERTY_PIN_POPOVER_TITLE)}
                      Title={() => (
                        <h3 class="flex items-center gap-x-2 text-header-md text-primary">
                          <span>{t(($) => $.FIELD_PROPERTY_SEARCH_COLORS_POPOVER_TITLE)}</span>
                          <span class="flex items-center justify-center rounded-sm bg-surface-subtle px-1.5 py-1 text-small-medium text-primary">
                            {t(($) => $.FIELD_PROPERTY_SEARCH_COLORS_POPOVER_QUERY_MATCHES)}
                          </span>
                        </h3>
                      )}
                      hasError={!isEmpty(issues())}
                      trigger={{
                        ariaLabel: t(($) => $.FIELD_PROPERTY_SEARCH_COLORS_EDIT_ARIA, { name: queryColour().query }),
                        label: queryColour().query,
                        hexColour: queryColour().color,
                      }}
                      secondaryAction={{
                        icon: "trash",
                        onClick: () => handleDelete(queryColour().id),
                        title: t(($) => $.FIELD_PROPERTY_PIN_DELETE),
                      }}
                    >
                      <div class="flex flex-col gap-y-4 pt-2">
                        <div class="flex flex-col gap-y-1">
                          <CompactTextInput
                            id={createUniqueId()}
                            ariaLabel={t(($) => $.FIELD_PROPERTY_SEARCH_QUERY_LABEL)}
                            value={queryColour().query}
                            placeholder={t(($) => $.TABLE_FILTER_TEXT_PLACEHOLDER)}
                            onBlur={(e) => handleChange(queryColour().id, { query: e.currentTarget.value })}
                            errorMessage={queryError()}
                          />
                          <ErrorMessage errorMessage={queryError()} />
                        </div>
                        <ColorInput
                          label={t(($) => $.FIELD_PROPERTY_SEARCH_COLOR_LABEL)}
                          onChange={(value) => handleChange(queryColour().id, { color: value })}
                          value={queryColour().color}
                        />
                      </div>
                    </SplitButtonProperty>
                  </li>
                );
              }}
            </Index>
          </ul>

          <Show
            when={props.queryColours.length < MAX_COLOURS}
            fallback={
              <InlineMessage
                variant="warning"
                label={t(($) => $.FIELD_PROPERTY_SEARCH_COLORS_MAX, { max: MAX_COLOURS })}
              />
            }
          >
            <TextAndIconButton
              icon="plus"
              text={t(($) => $.FIELD_PROPERTY_SEARCH_COLORS_ADD_BUTTON)}
              variant="light"
              onClick={handleAdd}
            />
          </Show>
        </>
      )}
    </Input>
  );
};

export default SearchColoursInput;
